import { CheckboxField, SelectField, TextField } from '~/common/components';

import { Trans } from '~/common/context';
import { memo } from 'react';
import {
  Col,
  Flex,
  Icon,
  Label,
  Row,
  Switch,
  Tooltip,
  useConfiguredForm,
} from '@wyz/ui';
import { ISupplierTemplateForm } from '~/connect/types';
import { useFormContext } from 'react-hook-form';
import { SUPPLIERS_STATUSES } from '~/connect/schemas';
import { useManageAlternativeSupplierRankingModel } from '~/connect/api';
import { SUPPLIERS_TYPES_IDS } from '@wyz/utils';

const DISABLED_BETTER_DELAY = [0];
const ENABLED_RANKING_MODEL_ACTIVE = 'ACTIVE';
interface Props {
  baseName:
    | `suppliers.${number}`
    | `bestSourcingSupplierConfigurations.${string}`;
  supplier: {
    id: number;
    orderable: boolean | null | undefined;
    childCustomerDelivery: boolean | null | undefined;
    status: string | null | undefined;
    type: number;
  };
  index: number;
  enableShowIfBetterDelay?: boolean;
  enableExtendedManuf?: boolean;
}

const SupplierDraggableItem = ({
  baseName,
  supplier,
  index,
  enableShowIfBetterDelay = true,
  enableExtendedManuf = true,
}: Props) => {
  const methods = useFormContext<ISupplierTemplateForm>();
  const configurationContext = useConfiguredForm();
  const extendedManufEnabled = methods.watch(
    `${baseName}.extendedManufConfiguration.enabled`,
  );

  const manageSupplierRankingModel = useManageAlternativeSupplierRankingModel({
    list: {
      enabled: true,
      args: {
        query: {
          status: ENABLED_RANKING_MODEL_ACTIVE,
        },
      },
    },
  });

  return (
    <>
      <Row>
        <Col>
          <Label htmlFor={`${supplier.id}-status`} className='capitalize mb-2'>
            <Trans i18nKey='app.supplier_template.form.supplier.active' />
          </Label>
        </Col>
        <Col>
          <Switch
            defaultChecked={supplier.status === SUPPLIERS_STATUSES.ENABLED}
            onChange={(event) => {
              methods.setValue(
                `${baseName}.status`,
                event.target.checked
                  ? SUPPLIERS_STATUSES.ENABLED
                  : SUPPLIERS_STATUSES.DISABLED,
              );
            }}
            disabled={configurationContext.mode === 'READ_ONLY'}
            id={`${supplier.id}-status`}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Label
            htmlFor={`${supplier.id}-orderable`}
            className='capitalize mb-2'
          >
            <Trans i18nKey='app.common.status.orderable' />
          </Label>
        </Col>
        <Col>
          <Switch
            defaultChecked={Boolean(supplier.orderable)}
            disabled={configurationContext.mode === 'READ_ONLY'}
            onChange={(event) => {
              methods.setValue(`${baseName}.orderable`, event.target.checked);
            }}
            id={`${supplier.id}-orderable`}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Label
            htmlFor={`${supplier.id}-child-customer-delivery`}
            className='capitalize mb-2'
          >
            <Trans i18nKey='app.supplier_template.form.supplier.child_customer_delivery' />
          </Label>
        </Col>
        <Col>
          <Switch
            id={`${supplier.id}-child-customer-delivery`}
            onChange={(event) => {
              methods.setValue(
                `${baseName}.childCustomerDelivery`,
                event.target.checked,
              );
            }}
            defaultChecked={Boolean(supplier.childCustomerDelivery)}
            disabled={configurationContext.mode === 'READ_ONLY'}
          />
        </Col>
      </Row>
      {enableShowIfBetterDelay && (
        <Row>
          <CheckboxField
            id={`${supplier.id}-display`}
            name={`${baseName}.overridePriorityIfShorterDelivery`}
            label={
              <Flex alignItems='center' className='space-x-2'>
                <span>
                  <Trans i18nKey='app.supplier_template.form.supplier.display' />
                </span>
                {DISABLED_BETTER_DELAY.includes(index) && (
                  <Tooltip
                    label={
                      <Trans i18nKey='app.supplier_template.form.supplier.override_priority_if_shorter_delivery_helper' />
                    }
                  >
                    <Icon icon='info-circle' size='xxs' />
                  </Tooltip>
                )}
              </Flex>
            }
            disabled={DISABLED_BETTER_DELAY.includes(index)}
          />
        </Row>
      )}
      <Row>
        <TextField
          id={`${supplier.id}-front-children-label`}
          name={`${baseName}.customName`}
          label={<Trans i18nKey='app.common.customer.custom_name' />}
        />
      </Row>
      {supplier.type === SUPPLIERS_TYPES_IDS.WYZ_DISTRIBUTOR && (
        <Row>
          <SelectField
            label={
              <Trans i18nKey='app.supplier_template.form.supplier.ranking_model' />
            }
            name={`${baseName}.rankingModelId`}
            options={
              manageSupplierRankingModel.list.data?.data.map((model) => ({
                value: model.id,
                label: model.name,
              })) ?? []
            }
            isLoading={manageSupplierRankingModel.list.isLoading}
            isClearable
          />
        </Row>
      )}
      {supplier.type === SUPPLIERS_TYPES_IDS.MANUFACTURER &&
        enableExtendedManuf && (
          <>
            <Row>
              <Col>
                <Label
                  htmlFor={`${supplier.id}-extended-manuf-enabled`}
                  className='capitalize mb-2'
                >
                  <Trans i18nKey='app.supplier_template.form.supplier.extended_manuf_configuration.enabled' />
                </Label>
              </Col>
              <Col>
                <Switch
                  id={`${supplier.id}-extended-manuf-enabled`}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    methods.setValue(
                      `${baseName}.extendedManufConfiguration.enabled`,
                      enabled,
                    );
                    if (!enabled) {
                      methods.setValue(
                        `${baseName}.extendedManufConfiguration.displayName`,
                        '',
                      );
                      methods.setValue(
                        `${baseName}.extendedManufConfiguration.warehouseId`,
                        '',
                      );
                    }
                  }}
                  defaultChecked={
                    !!methods.watch(
                      `${baseName}.extendedManufConfiguration.enabled`,
                    )
                  }
                  disabled={configurationContext.mode === 'READ_ONLY'}
                />
              </Col>
            </Row>
            {extendedManufEnabled && (
              <>
                <TextField
                  id={`${supplier.id}-extended-manuf-warehouse-id`}
                  name={`${baseName}.extendedManufConfiguration.warehouseId`}
                  label={
                    <Trans i18nKey='app.supplier_template.form.supplier.extended_manuf_configuration.warehouse_id' />
                  }
                  disabled={configurationContext.mode === 'READ_ONLY'}
                  required
                />
                <TextField
                  id={`${supplier.id}-extended-manuf-display-name`}
                  name={`${baseName}.extendedManufConfiguration.displayName`}
                  label={
                    <Trans i18nKey='app.supplier_template.form.supplier.extended_manuf_configuration.display_name' />
                  }
                  disabled={configurationContext.mode === 'READ_ONLY'}
                  required
                />
              </>
            )}
          </>
        )}
    </>
  );
};

export default memo(SupplierDraggableItem);
